import { useState } from "react";
import { Modal, Input, Label, Alert, Spinner } from "reactstrap";
import Select from "react-select";
import api from "../helpers/api";
import { countryOptions } from "../constants/utils";

const roleOptions = [
  { value: "ROLE_ADMINISTRATOR", label: "Administrator" },
  { value: "ROLE_FULL_USER", label: "Full User" },
  { value: "ROLE_REQUESTOR", label: "Requestor" },
];

const InviteUserModal = ({ isOpen, toggle, onSuccess }) => {
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitePhoneCode, setInvitePhoneCode] = useState("");
  const [invitePhone, setInvitePhone] = useState("");
  const [inviteRole, setInviteRole] = useState("ROLE_REQUESTOR");
  const [createLoading, setCreateLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const resetForm = () => {
    setInviteName("");
    setInviteEmail("");
    setInvitePhoneCode("");
    setInvitePhone("");
    setInviteRole("ROLE_REQUESTOR");
    setCreateLoading(false);
    setApiError("");
  };

  const handleClose = () => {
    resetForm();
    toggle();
  };

  const handleCreate = async () => {
    setApiError("");
    setCreateLoading(true);

    try {
      if (!inviteName.trim()) {
        setApiError("Name is required");
        setCreateLoading(false);
        return;
      }
      if (!inviteEmail.trim()) {
        setApiError("Email is required");
        setCreateLoading(false);
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail.trim())) {
        setApiError("Please enter a valid email address");
        setCreateLoading(false);
        return;
      }

      const payload = {
        name: inviteName.trim(),
        email: inviteEmail.trim(),
        targetRole: inviteRole,
      };

      if (invitePhone.trim()) {
        payload.phoneCode = invitePhoneCode.trim();
        payload.contactNumber = invitePhone.trim();
      }

      await api.post("invitation/create", payload);

      resetForm();
      if (onSuccess) onSuccess();
      toggle();
    } catch (error) {
      setApiError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to send invitation"
      );
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="md">
      <div className="modal-header">
        <h5 className="modal-title">Invite User</h5>
        <button
          type="button"
          className="btn-close"
          onClick={handleClose}
          aria-label="Close"
        />
      </div>
      <div className="modal-body">
        {apiError && (
          <Alert color="danger" className="mb-3">
            {apiError}
          </Alert>
        )}

        <div className="mb-3">
          <Label className="form-label">Name</Label>
          <Input
            type="text"
            placeholder="Enter name"
            value={inviteName}
            onChange={(e) => setInviteName(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <Label className="form-label">Email Address</Label>
          <Input
            type="email"
            placeholder="Enter email address"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <Label className="form-label">
            Phone Number <span className="text-muted">(optional)</span>
          </Label>
          <div className="d-flex gap-2">
            <Select
              options={countryOptions}
              value={countryOptions.find((c) =>
                invitePhoneCode === c.dialCode.replace("+", "")
              ) || null}
              onChange={(selected) => {
                setInvitePhoneCode(selected?.dialCode.replace("+", "") || "");
              }}
              filterOption={(option, inputValue) => {
                if (!inputValue) return true;
                const searchText = inputValue.toLowerCase();
                const labelText = option.data.labelText?.toLowerCase() || "";
                const name = option.data.name?.toLowerCase() || "";
                const dialCode = option.data.dialCode?.toLowerCase() || "";
                return labelText.includes(searchText) ||
                  name.includes(searchText) ||
                  dialCode.includes(searchText);
              }}
              className="flex-grow-0"
              styles={{
                control: (base) => ({
                  ...base,
                  width: "150px",
                }),
              }}
              isClearable
              placeholder="Code"
            />
            <Input
              type="tel"
              className="flex-grow-1"
              placeholder="Phone number"
              value={invitePhone}
              onChange={(e) => setInvitePhone(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-3">
          <Label className="form-label">Role</Label>
          <select
            className="form-select"
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
          >
            {roleOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="modal-footer">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-success"
          onClick={handleCreate}
          disabled={createLoading}
        >
          {createLoading ? (
            <>
              <Spinner size="sm" className="me-1" /> Sending...
            </>
          ) : (
            "Send Invitation"
          )}
        </button>
      </div>
    </Modal>
  );
};

export default InviteUserModal;
